import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PowerOff, ShieldAlert } from 'lucide-react';
import type { FeatureKey } from '@/types';
import type { Permission } from '@/lib/permissions';
import { ServiceError } from '@/services';
import { useAuthStore } from '@/app/stores';
import { ButtonLink } from '@/components/ui/Button';
import { EmptyState, ErrorState, PageLoader } from '@/components/ui/States';
import { useAdminSession } from './session';

export function AccessDenied({ title = "You don't have access to this area", description }: { title?: string; description?: string }) {
  return (
    <EmptyState
      icon={ShieldAlert} title={title}
      description={description ?? 'Your account does not have permission to view this page. Ask a store admin if you need access.'}
      action={<div className="flex flex-wrap justify-center gap-2"><ButtonLink to="/admin" variant="outline">Admin home</ButtonLink><ButtonLink to="/" variant="ghost">Back to store</ButtonLink></div>}
      className="py-24"
    />
  );
}

/**
 * Layout-level gate for /admin. UX ONLY: it decides what to render. The API must independently reject
 * unauthenticated / unauthorised / cross-tenant requests — a hidden page is not a protected resource.
 */
export function AdminGate({ children }: { children: ReactNode }) {
  const { user, ready } = useAuthStore();
  const { actor, error, loading, load, reset } = useAdminSession();
  const loc = useLocation();

  useEffect(() => {
    if (!ready) return;
    if (!user) { reset(); return; }
    void load(); // re-check with the "server" whenever the signed-in identity changes
  }, [user?.id, user?.role, ready, load, reset]);

  if (!ready) return <PageLoader label="Loading…" />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (loading && !actor) return <PageLoader label="Loading your workspace…" />;
  if (error) {
    if (error instanceof ServiceError && error.code === 'FORBIDDEN') {
      return <AccessDenied title="This area is for store staff" description="You are signed in as a customer. Sign in with a store admin or staff account to manage a store." />;
    }
    if (error instanceof ServiceError && error.code === 'UNAUTHORIZED') return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
    return <ErrorState error={error} onRetry={() => void load()} />;
  }
  if (!actor) return <PageLoader />;
  return <>{children}</>;
}

/** Route-level permission + module guard. Mirrors the API's requirePermission + requireFeature middleware. */
export function AdminRoute({ permission, feature, children }: { permission: Permission; feature?: FeatureKey; children: ReactNode }) {
  const actor = useAdminSession((s) => s.actor);
  if (!actor) return null;
  if (!actor.permissions.includes(permission)) {
    return <AccessDenied description={`Your role (${actor.user.role.replace('_', ' ').toLowerCase()}) is not allowed to open this page.`} />;
  }
  if (feature && !actor.tenant.features[feature]) {
    return (
      <EmptyState
        icon={PowerOff} title="This module is turned off for your store"
        description="It has been disabled on your plan. The platform administrator can enable it — nothing here is reachable until then."
        action={<ButtonLink to="/admin" variant="outline">Back to dashboard</ButtonLink>} className="py-24"
      />
    );
  }
  return <>{children}</>;
}
